/**
 * Grants on the Character (issue #6). The engine says what every GrantItem and feature list
 * wants (`sheet.grants`); this module makes the Character match: a granted Entry becomes the
 * Selection of a Choice keyed by the granting Entry (`rule-element` or `feature` source) on the
 * Level that opened it, and leaves when its granter does. Pure functions; the store and the
 * Entry loader are the caller's (see `materialiseGrants` in ./index.ts).
 *
 * Foundry evaluates a GrantItem once, when the granter is created. Here every grant is derived
 * from the progression log on each pass, so a predicate that later passes grants and one that
 * later fails removes (docs/design.md, Rules engine). `onDeleteActions` are honoured when a
 * Selection is cleared: `restrict` blocks, `detach` keeps the granted Choice, `cascade` (the
 * default) removes it with everything it granted in turn.
 */
import type { Entry } from "../../shared/snapshot-format";
import type { Catalogue } from "../engine/catalogue";
import { deriveSheet, type DerivedSheet, type EngineInput, type GrantView } from "../engine/engine";
import { ensureLevels } from "./create";
import type { AnswerValue, Character, Choice, Level, LevelNumber, Selection } from "./model";
import { choiceKey, findChoice, isGranted } from "./model";

export interface ReconcileInput {
  character: Character;
  entries: ReadonlyMap<string, Entry>;
  catalogue?: Catalogue | undefined;
  /** The Session's Conditions and Effects, so grants whose predicates read them settle with them in force (#18). */
  extra?: EngineInput["extra"];
  toggleState?: EngineInput["toggleState"];
}

export interface ReconcileResult {
  character: Character;
  /** Whether any Choice was added, removed or changed. */
  changed: boolean;
  /** Entry UUIDs the engine needs to finish (granted Entries not yet loaded, in-memory Conditions). */
  needed: string[];
  /** The sheet derived from the returned Character. */
  sheet: DerivedSheet;
  /** How many passes it took; MAX_PASSES means the grants did not settle (a Rule Element pair that toggles each other). */
  passes: number;
}

export const MAX_PASSES = 8;

/** Whether two answer values are the same. */
function sameAnswer(a: AnswerValue | undefined, b: AnswerValue | undefined): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Every Choice with its Level, in Character order. */
function allChoices(character: Character): { level: Level; choice: Choice; key: string }[] {
  return character.levels.flatMap((level) => level.choices.map((choice) => ({ level, choice, key: choiceKey(level.number, choice.source) })));
}

/**
 * One pass: derive, then add the grants the engine wants and remove the granted Choices it no
 * longer wants. Returns a new Character when anything changed.
 */
export function reconcileOnce(input: ReconcileInput): ReconcileResult {
  const catalogue = input.catalogue;
  const engineInput: EngineInput = { character: input.character, entries: input.entries };
  if (catalogue) engineInput.catalogue = catalogue;
  if (input.extra) engineInput.extra = input.extra;
  if (input.toggleState) engineInput.toggleState = input.toggleState;
  const sheet = deriveSheet(engineInput);
  const character = structuredClone(input.character);
  let changed = false;
  const wanted = new Map<string, GrantView>();
  for (const grant of sheet.grants) if (grant.wanted && grant.uuid && !grant.stopped) wanted.set(grant.key, grant);

  // Remove stale granted Choices: no longer wanted, unless detached.
  for (const level of character.levels) {
    const kept: Choice[] = [];
    for (const choice of level.choices) {
      if (!isGranted(choice.source) || choice.detached) {
        kept.push(choice);
        continue;
      }
      const key = choiceKey(level.number, choice.source);
      const grant = wanted.get(key);
      if (!grant) {
        changed = true;
        continue;
      }
      if (choice.selection?.uuid !== grant.uuid) {
        // The granter now resolves to a different Entry (its ChoiceSet was re-answered): replace the Selection.
        kept.push({ source: choice.source, selection: { uuid: grant.uuid!, snapshotTag: character.snapshotTag }, answers: { ...grant.preselect } });
        changed = true;
        continue;
      }
      // Keep the user's answers; refresh preselected ones the granter dictates.
      const answers = { ...(choice.answers ?? {}) };
      let touched = false;
      for (const [flag, value] of Object.entries(grant.preselect)) {
        if (!sameAnswer(answers[flag], value)) {
          answers[flag] = value;
          touched = true;
        }
      }
      if (touched) {
        changed = true;
        kept.push({ ...choice, answers });
      } else kept.push(choice);
    }
    level.choices = kept;
  }

  // Add wanted grants the Character does not hold.
  const held = new Set(allChoices(character).map((c) => c.key));
  const uuidsHeld = new Map<string, string[]>();
  for (const { choice, key } of allChoices(character)) {
    if (choice.selection) uuidsHeld.set(choice.selection.uuid, [...(uuidsHeld.get(choice.selection.uuid) ?? []), key]);
  }
  let next = character;
  for (const grant of wanted.values()) {
    if (held.has(grant.key)) continue;
    // Foundry: with allowDuplicate off, an Entry the Character already holds is not granted again.
    if (!grant.allowDuplicate && (uuidsHeld.get(grant.uuid!) ?? []).some((k) => k !== grant.key)) continue;
    next = ensureLevels(next, grant.level);
    const level = next.levels[grant.level - 1]!;
    const selection: Selection = { uuid: grant.uuid!, snapshotTag: next.snapshotTag };
    const choice: Choice = { source: grant.source, selection, answers: { ...grant.preselect } };
    // Right after the granter when it sits on the same Level, else at the end.
    const granterIndex = grant.granter.choiceKey ? level.choices.findIndex((c) => choiceKey(level.number, c.source) === grant.granter.choiceKey) : -1;
    const at = granterIndex === -1 ? level.choices.length : granterIndex + 1 + level.choices.slice(granterIndex + 1).findIndex((c) => !isGranted(c.source) || c.detached) ;
    level.choices.splice(at < granterIndex + 1 ? level.choices.length : at, 0, choice);
    held.add(grant.key);
    uuidsHeld.set(grant.uuid!, [...(uuidsHeld.get(grant.uuid!) ?? []), grant.key]);
    changed = true;
  }

  const needed = [...new Set(sheet.needed)].filter((uuid) => !input.entries.has(uuid));
  return { character: changed ? next : input.character, changed, needed, sheet, passes: 1 };
}

/**
 * Reconcile until the grants settle or MAX_PASSES is reached. Stops early when an Entry the
 * engine needs is not loaded: load `needed` and call again.
 */
export function reconcileGrants(input: ReconcileInput): ReconcileResult {
  let character = input.character;
  let changed = false;
  let result = reconcileOnce({ ...input, character });
  let passes = 1;
  while (result.changed && result.needed.length === 0 && passes < MAX_PASSES) {
    changed = true;
    character = result.character;
    result = reconcileOnce({ ...input, character });
    passes++;
  }
  if (result.changed) {
    changed = true;
    character = result.character;
  }
  // The sheet must describe the Character returned.
  const final = result.changed ? reconcileOnce({ ...input, character }) : result;
  return { character, changed, needed: final.needed, sheet: final.sheet, passes };
}

/* ------------------------------ Selection changes ------------------------------ */

export interface ChangeResult {
  character: Character;
  /** When set, nothing changed: a grant with `restrict` forbids it, or the key names no Choice. */
  blocked: string | null;
}

/** The grants beneath a Choice, recursively, from the engine's view. */
function grantsUnder(sheet: DerivedSheet, key: string): GrantView[] {
  const out: GrantView[] = [];
  const walk = (parent: string): void => {
    for (const grant of sheet.grants) {
      if (grant.granter.choiceKey !== parent || !grant.present) continue;
      out.push(grant);
      walk(grant.key);
    }
  };
  walk(key);
  return out;
}

/**
 * Clear a Choice's Selection (and answers). Grants beneath it follow their `onDeleteActions.granter`:
 * `restrict` blocks the change, `detach` keeps the granted Choice as its own Selection, `cascade`
 * lets the next reconcile remove it. A granted Choice itself cannot be cleared on its own: with
 * `grantee: cascade` its granter is cleared instead, otherwise the change is blocked.
 */
export function clearSelection(character: Character, key: string, sheet: DerivedSheet): ChangeResult {
  const choice = findChoice(character, key);
  if (!choice) return { character, blocked: `no Choice ${key}` };
  if (isGranted(choice.source) && !choice.detached) {
    const grant = sheet.grants.find((g) => g.key === key);
    if (grant?.onDelete.grantee === "cascade" && grant.granter.choiceKey) return clearSelection(character, grant.granter.choiceKey, sheet);
    return { character, blocked: `${key} was granted by ${grant?.granter.name ?? "another Entry"}; clear that Selection instead` };
  }
  const beneath = grantsUnder(sheet, key);
  const restricted = beneath.find((g) => g.onDelete.granter === "restrict");
  if (restricted) return { character, blocked: `${restricted.name ?? restricted.uuid} (granted by ${restricted.granter.name}) does not allow its granter to be removed` };
  const next = structuredClone(character);
  const detach = new Set(beneath.filter((g) => g.onDelete.granter === "detach").map((g) => g.key));
  for (const level of next.levels) {
    for (const c of level.choices) {
      const k = choiceKey(level.number, c.source);
      if (k === key) {
        delete c.selection;
        delete c.answers;
      } else if (detach.has(k)) c.detached = true;
    }
  }
  return { character: next, blocked: null };
}

/** Fill (or replace) a Choice's Selection. The Choice must exist; a granted one cannot be filled by hand. */
export function setSelection(character: Character, key: string, uuid: string, answers: Record<string, AnswerValue> = {}, sheet?: DerivedSheet): ChangeResult {
  const existing = findChoice(character, key);
  if (!existing) return { character, blocked: `no Choice ${key}` };
  if (isGranted(existing.source) && !existing.detached) return { character, blocked: `${key} is filled by its granter` };
  let base = character;
  if (existing.selection && sheet) {
    const cleared = clearSelection(character, key, sheet);
    if (cleared.blocked) return cleared;
    base = cleared.character;
  }
  const next = structuredClone(base);
  for (const level of next.levels) {
    for (const c of level.choices) {
      if (choiceKey(level.number, c.source) !== key) continue;
      c.selection = { uuid, snapshotTag: next.snapshotTag };
      c.answers = { ...answers };
      delete c.reopened;
    }
  }
  return { character: next, blocked: null };
}

/** Answer (or re-answer) a prompt on a Choice's Selection: a ChoiceSet flag, a boost, a key ability. */
export function setAnswer(character: Character, key: string, flag: string, value: AnswerValue | null): ChangeResult {
  const existing = findChoice(character, key);
  if (!existing) return { character, blocked: `no Choice ${key}` };
  const next = structuredClone(character);
  for (const level of next.levels) {
    for (const c of level.choices) {
      if (choiceKey(level.number, c.source) !== key) continue;
      const answers = { ...(c.answers ?? {}) };
      if (value === null) delete answers[flag];
      else answers[flag] = value;
      c.answers = answers;
      // An answered flag no longer needs its migration warning (#17).
      if (value !== null && c.reopened) {
        const left = c.reopened.filter((r) => r.flag !== flag);
        if (left.length > 0) c.reopened = left;
        else delete c.reopened;
      }
    }
  }
  return { character: next, blocked: null };
}

/** The Level a key names, for callers that place Choices. */
export function levelOfKey(key: string): LevelNumber | null {
  const n = Number(key.split(":")[0]);
  return Number.isInteger(n) && n >= 1 && n <= 20 ? (n as LevelNumber) : null;
}
